import { t } from "../../translations";

import "../styles/section-chef.css";

import { useState } from "react";
import { GET } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";

import { CircularProgress } from "@mui/material";
import Papa from "papaparse";
import { useEffect } from "react";
import { createLogger } from "../../utils/logger";
import TableWithdrawal from "../tables/table-withdrawal";
import { openNotificationWithIcon } from "./notification-bar";

const logger = createLogger("SelectWithdrawal");

const WITHDRAWAL_STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled"
};

const ORDER_STATUS = {
  WAITING: 0,
  READY: 1,
  ON_WAY: 2,
  DELIVERED: 3,
  CANCELED: 4,
  PENDING_REFUND: 5,
  REFUNDED: 6
};

const SelectWithdrawal = (props) => {
  const { lang, user } = props;

  const [withdrawalList, setWithdrawalList] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);

  const [load, setLoad] = useState(false);

  useEffect(() => {
    initialData();
  }, [user?._id, user?.refresh_token]); // Re-run when user ID or token changes

  const validateResponse = (response) => {
    if (!response?.data) {
      throw new Error("No data in response");
    }

    try {
      // Test if response data is valid JSON
      JSON.parse(JSON.stringify(response.data));
      return true;
    } catch (_e) {
      throw new Error("Invalid JSON in response");
    }
  };

  const initialData = async () => {
    if (!user?._id || !user?.refresh_token) {
      logger.error("Missing user ID or token:", {
        id: user?._id,
        hasToken: !!user?.refresh_token
      });
      return;
    }

    setLoad(true);

    try {
      // Log request parameters
      logger.debug("Fetching data for user:", {
        id: user._id,
        hasToken: !!user.refresh_token,
        api: service_api
      });

      // Fetch orders and withdrawals concurrently for better performance
      logger.debug(
        "🚀 Fetching completed orders and withdrawals concurrently:",
        {
          userId: user._id,
          ordersEndpoint: `${service_api}/order/get-chef-orders-completed/${user._id}`,
          withdrawalsEndpoint: `${service_api}/withdrawal/get-entity-chef-withdrawal/${user._id}`,
          hasToken: !!user.refresh_token
        }
      );

      const [ordersResponse, withdrawalResponse, walletResponse] =
        await Promise.all([
          MakeRequestAsync({
            type: GET,
            url: service_api,
            route: `order/get-chef-orders-completed/${user._id}`,
            data: null,
            token: user.refresh_token
          }),
          MakeRequestAsync({
            type: GET,
            url: service_api,
            route: `withdrawal/get-entity-chef-withdrawal/${user._id}`,
            data: null,
            token: user.refresh_token
          }),
          MakeRequestAsync({
            type: GET,
            url: service_api,
            route: `wallet/user/${user._id}`,
            data: null,
            token: user.refresh_token
          })
        ]);

      // Validate all responses
      validateResponse(ordersResponse);
      validateResponse(withdrawalResponse);
      validateResponse(walletResponse);

      // Process orders response
      logger.debug("📦 Orders API Response:", {
        status: ordersResponse?.status,
        statusText: ordersResponse?.statusText,
        hasData: !!ordersResponse?.data,
        dataType: typeof ordersResponse?.data
      });

      logger.debug("📝 Orders Response Headers:", ordersResponse?.headers);

      if (ordersResponse?.data?.order) {
        logger.debug("📊 Orders Data Stats:", {
          count: ordersResponse.data.order.length,
          hasOrders: ordersResponse.data.order.length > 0,
          firstOrderDate: ordersResponse.data.order[0]?.createdAt,
          lastOrderDate:
            ordersResponse.data.order[ordersResponse.data.order.length - 1]
              ?.createdAt
        });

        const orders = ordersResponse.data.order;
        logger.debug("Raw orders data:", orders);
        logger.debug("Sample order structure:", orders?.[0]);
        logger.debug("Sample billing details:", orders?.[0]?.billing_details);
        setOrderList(Array.isArray(orders) ? orders : []);
      } else {
        logger.debug("No orders data in response:", ordersResponse?.data);
      }

      logger.debug("💰 Withdrawals API Response:", {
        status: withdrawalResponse?.status,
        statusText: withdrawalResponse?.statusText,
        hasData: !!withdrawalResponse?.data,
        dataType: typeof withdrawalResponse?.data
      });

      logger.debug(
        "📝 Withdrawals Response Headers:",
        withdrawalResponse?.headers
      );

      if (withdrawalResponse?.data?.withdrawal) {
        // Decrypt withdrawal data
        const decryptedWithdrawals = decryptSingleData(
          withdrawalResponse.data.withdrawal
        );
        logger.debug("📊 Withdrawals Data Stats:", {
          count: decryptedWithdrawals.length,
          hasWithdrawals: decryptedWithdrawals.length > 0,
          totalAmount: decryptedWithdrawals.reduce(
            (sum, w) => sum + (w.amount || 0),
            0
          ),
          statusCounts: decryptedWithdrawals.reduce((acc, w) => {
            acc[w.status] = (acc[w.status] || 0) + 1;
            return acc;
          }, {}),
          firstWithdrawalDate: decryptedWithdrawals[0]?.createdAt,
          lastWithdrawalDate:
            decryptedWithdrawals[decryptedWithdrawals.length - 1]?.createdAt
        });

        // Log sample withdrawal for debugging
        if (decryptedWithdrawals.length > 0) {
          logger.debug("📋 Sample Withdrawal:", {
            id: decryptedWithdrawals[0]._id,
            amount: decryptedWithdrawals[0].amount,
            status: decryptedWithdrawals[0].status,
            createdAt: decryptedWithdrawals[0].createdAt,
            accountDetails: decryptedWithdrawals[0].account_detail
          });
        }

        logger.debug("Decrypted withdrawals data:", decryptedWithdrawals);
        setWithdrawalList(
          Array.isArray(decryptedWithdrawals) ? decryptedWithdrawals : []
        );

        // Process wallet response
        logger.debug("💰 Wallet API Response:", {
          status: walletResponse?.status,
          statusText: walletResponse?.statusText,
          hasData: !!walletResponse?.data,
          dataType: typeof walletResponse?.data
        });

        if (walletResponse?.data?.success && walletResponse?.data?.data) {
          const walletData = walletResponse.data.data;
          const availableFunds =
            walletData.availableFunds || walletData.balance || 0;
          const earnings = walletData.totalEarnings || 0;
          const withdrawn = walletData.totalWithdrawn || 0;

          logger.debug("💰 Wallet Data:", {
            totalEarnings: earnings,
            totalWithdrawn: withdrawn,
            availableFunds: availableFunds
          });

          setTotalEarnings(earnings);
          setTotalWithdrawn(withdrawn);
          setWalletBalance(availableFunds);
        } else {
          logger.warn("💰 No wallet data found, defaulting to 0");
          setTotalEarnings(0);
          setTotalWithdrawn(0);
          setWalletBalance(0);
        }
      }
    } catch (error) {
      logger.error("❌ API Error Details:", {
        error: {
          message: error?.message,
          name: error?.name,
          type: error?.constructor?.name,
          stack: error?.stack?.split("\n").slice(0, 3) // First 3 lines of stack trace
        },
        response: {
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data,
          headers: error?.response?.headers,
          type: error?.response?.type,
          size: error?.response?.data
            ? JSON.stringify(error?.response?.data).length
            : 0
        },
        request: {
          url: error?.config?.url,
          method: error?.config?.method,
          headers: error?.config?.headers,
          data: error?.config?.data,
          timeout: error?.config?.timeout,
          timestamp: new Date().toISOString()
        },
        context: {
          userId: user?._id,
          hasToken: !!user?.refresh_token,
          apiUrl: service_api
        }
      });

      // Show error to user
      openNotificationWithIcon(
        "error",
        error?.message || t("status.an_error_occure", lang)
      );

      // Reset lists to empty arrays
      setOrderList([]);
      setWithdrawalList([]);
    } finally {
      setLoad(false);
    }
  };

  const getStatusText = (status, type) => {
    if (type === "order") {
      switch (status) {
        case ORDER_STATUS.WAITING:
          return t("common.waiting_4", lang);
        case ORDER_STATUS.READY:
          return t("common.ready_for_deliv", lang);
        case ORDER_STATUS.ON_WAY:
          return t("common.on_way_4", lang);
        case ORDER_STATUS.DELIVERED:
          return t("common.delivery_comple", lang);
        case ORDER_STATUS.CANCELED:
          return t("common.annul_e", lang);
        case ORDER_STATUS.PENDING_REFUND:
          return t("common.waiting_for_ref_2", lang);
        case ORDER_STATUS.REFUNDED:
          return t("common.refunded_2", lang);
        default:
          return t("common.unknown", lang);
      }
    } else {
      // Withdrawal statuses
      switch (status) {
        case WITHDRAWAL_STATUS.PENDING:
          return t("common.waiting_6", lang);
        case WITHDRAWAL_STATUS.PROCESSING:
          return t("common.processing", lang);
        case WITHDRAWAL_STATUS.COMPLETED:
          return t("common.completed_4", lang);
        case WITHDRAWAL_STATUS.FAILED:
          return t("common.failed", lang);
        case WITHDRAWAL_STATUS.CANCELLED:
          return t("common.annul_e", lang);
        default:
          return t("common.unknown", lang);
      }
    }
  };

  const exportToCSV = () => {
    if (load || (!withdrawalList.length && !orderList.length)) {
      openNotificationWithIcon("info", t("status.no_data_to_export", lang));
      return;
    }

    const combinedData = [
      ...orderList.map((order) => ({
        ...order,
        type: "order",
        amount: order.billing_details?.earned_chef || 0
      })),
      ...withdrawalList.map((withdrawal) => ({
        ...withdrawal,
        type: "withdrawal"
      }))
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const dataToExport = combinedData.map((item) => {
      const baseData = {
        Date: new Date(item.createdAt).toLocaleString(
          lang === "en" ? "en-US" : "fr-FR"
        ),
        Type: item.type === "order" ? "Earning" : "Payout",
        "Transaction ID": item._id,
        Amount:
          item.type === "order"
            ? item.amount.toFixed(2)
            : -item.amount.toFixed(2),
        Status: getStatusText(item.status, item.type)
      };

      if (item.type === "withdrawal") {
        return {
          ...baseData,
          "Bank Name": item.account_detail?.bank_name,
          "Account Name": item.account_detail?.account_name,
          "Account Number": `****${item.account_detail?.account_number.slice(
            -4
          )}`
        };
      }

      return {
        ...baseData,
        "Bank Name": "",
        "Account Name": "",
        "Account Number": ""
      };
    });

    const csv = Papa.unparse(dataToExport);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "transaction-history.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="section-chef-menu-right-food">
      <div className="section-chef-menu-right-food-inside">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}
        >
          <div
            className="section-chef-menu-right-food-inside-filter"
            style={{
              width: "100%",
              height: "auto",
              padding: "20px"
            }}
          >
            <p
              style={{
                textAlign: "left",
                fontSize: 20,
                fontWeight: 600,
                marginBottom: "25px",
                borderBottom: "1px solid #eee",
                paddingBottom: "10px"
              }}
            >
              {t("common.wallet_overview", lang)}
            </p>

            <div
              style={{
                display: "flex",
                gap: "30px",
                flexWrap: "wrap",
                marginBottom: "30px"
              }}
            >
              {/* Total Earnings */}
              <div style={{ flex: "1 1 300px" }}>
                <p
                  style={{
                    textAlign: "left",
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "5px"
                  }}
                >
                  {t("common.total_earning", lang)}
                </p>
                <p
                  style={{
                    textAlign: "left",
                    fontSize: 13,
                    color: "#666",
                    marginBottom: "10px"
                  }}
                >
                  {t("common.total_earnings_description", lang)}
                </p>
                <p
                  style={{
                    textAlign: "left",
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#2e7d32"
                  }}
                >
                  {"$" + totalEarnings.toFixed(2)}
                </p>
              </div>

              {/* Total Withdrawn */}
              <div style={{ flex: "1 1 300px" }}>
                <p
                  style={{
                    textAlign: "left",
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "5px"
                  }}
                >
                  {t("common.total_withdrawn", lang)}
                </p>
                <p
                  style={{
                    textAlign: "left",
                    fontSize: 13,
                    color: "#666",
                    marginBottom: "10px"
                  }}
                >
                  {t("common.total_withdrawn_description", lang)}
                </p>
                <p
                  style={{
                    textAlign: "left",
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#d32f2f"
                  }}
                >
                  {"$" + totalWithdrawn.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Available Funds */}
            <div
              style={{
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "20px"
                }}
              >
                <div>
                  <p
                    style={{
                      textAlign: "left",
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "5px"
                    }}
                  >
                    {t("common.available_funds", lang)}
                  </p>
                  <p
                    style={{
                      textAlign: "left",
                      fontSize: 13,
                      color: "#666"
                    }}
                  >
                    {t("common.available_funds_description", lang)}
                  </p>
                </div>
                <p
                  style={{
                    textAlign: "right",
                    fontSize: 36,
                    fontWeight: "bold",
                    color: "#1976d2",
                    margin: 0
                  }}
                >
                  {"$" + walletBalance.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          className="section-chef-menu-right-food-inside-filter"
          style={{
            width: "100%",
            height: "auto",
            marginTop: 30,
            padding: "20px",
            textAlign: "center"
          }}
        >
          <div style={{ marginBottom: "30px" }}>
            <div
              style={{
                fontSize: "48px",
                marginBottom: "20px",
                color: "#4CAF50"
              }}
            >
              💳
            </div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 600,
                marginBottom: "15px",
                color: "#333"
              }}
            >
              {t("common.automatic_payments", lang)}
            </h2>
            <p
              style={{
                fontSize: "16px",
                color: "#666",
                lineHeight: "1.5",
                marginBottom: "20px"
              }}
            >
              {t("common.automatic_payments_description", lang)}
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#f8f9fa",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "25px",
              textAlign: "left"
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: 600,
                marginBottom: "15px",
                color: "#333"
              }}
            >
              {t("common.how_it_works", lang)}
            </h3>
            <ul
              style={{
                paddingLeft: "20px",
                color: "#555",
                lineHeight: "1.6"
              }}
            >
              <li style={{ marginBottom: "8px" }}>
                {t("common.weekly_earnings_calculated", lang)}
              </li>
              <li style={{ marginBottom: "8px" }}>
                {t("common.payments_processed_friday", lang)}
              </li>
              <li style={{ marginBottom: "8px" }}>
                {t("common.funds_appear_2_3_days", lang)}
              </li>
              <li>{t("common.email_notifications", lang)}</li>
            </ul>
          </div>

          <div
            style={{
              backgroundColor: "#e3f2fd",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "25px"
            }}
          >
            <p
              style={{
                fontSize: "14px",
                color: "#1976d2",
                margin: 0,
                fontWeight: 500
              }}
            >
              💡 {t("common.update_bank_details", lang)}
            </p>
          </div>
        </div>
        <div
          className="section-chef-menu-right-food-inside-filter"
          style={{
            width: "100%",
            height: "auto",
            marginTop: 30
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20
            }}
          >
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
              {t("common.withdrawal_history", lang)}
            </h2>
            <div
              className="btn-get-started"
              style={{
                backgroundColor: "#fff",
                color: "blue",
                width: 150
              }}
              onClick={exportToCSV}
            >
              {!load && t("common.export_csv_exce_2", lang)}
              {load && <CircularProgress size={25} />}
            </div>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center"
            }}
          >
            {!load ? (
              <TableWithdrawal
                ordersData={orderList || []}
                withdrawalsData={withdrawalList || []}
              />
            ) : (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <CircularProgress size={40} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectWithdrawal;
